import { Fragment, lazy } from "react";
import { Navigate, Route } from "react-router-dom";
import ProtectedRoute from "./ProtextedRoute";
import { routes } from "@/config/routes";
import { AccountType, UserRole } from "@/types";

// Command Center Logs
const CCLogs = lazy(() => import("@/pages/CommandCenter/Logs"));
const CCLogDetails = lazy(() => import("@/pages/CommandCenter/Logs/Details"));

// Command Center Orders
const CCOrders = lazy(() => import("@/pages/CommandCenter/Orders"));
const CCOrderDetails = lazy(() => import("@/pages/CommandCenter/Orders/Details"));
const CCShipping = lazy(() => import("@/pages/CommandCenter/Shipping"));
const CCQuotes = lazy(() => import("@/pages/CommandCenter/Quotes"));
const CCQuoteDetails = lazy(() => import("@/pages/CommandCenter/Quotes/Details"));
const CCQuoteCheckout = lazy(() => import("@/pages/Quotes/Checkout"));
const CCProposals = lazy(() => import("@/pages/Proposals"));
const CCProjects = lazy(() => import("@/pages/Projects"));
const CCCalendar = lazy(() => import("@/pages/CommandCenter/Calendar"));
const CCProjectCreate = lazy(() => import("@/pages/Projects/Create"));
const CCProjectDetails = lazy(() => import("@/pages/Projects/Details"));
const CCProjectWorkOrderDetails = lazy(() => import("@/pages/Projects/WorkOrders/Details"));
// Local-testing-only nav entry for the redesigned PM project page — see
// docs/superpowers/specs/2026-07-31-cc-projects-preview-nav-design.md
const CCProjectsPreviewList = lazy(() => import("@/pages/Projects/PreviewList"));
const CCProjectsPreviewDetails = lazy(() => import("@/pages/Projects/PreviewDetails"));
const CCInstallerOrgs = lazy(() => import("@/pages/CommandCenter/InstallerOrgs"));
const CCInstallerOrgCreate = lazy(() => import("@/pages/CommandCenter/InstallerOrgs/Create"));
const CCInstallerOrgDetails = lazy(() => import("@/pages/CommandCenter/InstallerOrgs/Details"));
// Command Center Accounts

const AccountList = lazy(() => import('@/pages/CommandCenter/Accounts'));
const AccountDetails = lazy(() => import("@/pages/CommandCenter/Accounts/Details"));
const CreateAccount = lazy(() => import("@/pages/CommandCenter/Accounts/Create"));
// Command Center Leads
const CCLeads = lazy(() => import("@/pages/CommandCenter/Leads"));
const CCLeadDetails = lazy(() => import("@/pages/CommandCenter/Leads/Details"));
const CCConvertLead = lazy(() => import("@/pages/CommandCenter/Leads/Convert"));
const CreateLead = lazy(() => import("@/pages/Leads/Create"));
const UploadLeads = lazy(() => import("@/pages/Leads/Upload"));
// Command Center Opportunities
const CCOpportunities = lazy(() => import("@/pages/CommandCenter/Opportunities"));
const CCOpportunityDetails = lazy(() => import("@/pages/CommandCenter/Opportunities/Details"));
const CCOpportunityCreate = lazy(() => import("@/pages/CommandCenter/Opportunities/Create"));

// Command Center Users
const CCUsers = lazy(() => import("@/pages/CommandCenter/Users"));
const CCAddEditUser = lazy(() => import("@/pages/CommandCenter/Users/AddEditUser"));
const CCUserDetails = lazy(() => import("@/pages/CommandCenter/Users/Details"));

// Dealer Discount routes
const DealerDiscountsList = lazy(
    () => import("@/pages/CommandCenter/DealerDiscounts")
);
const AddDiscountCode = lazy(
    () => import("@/pages/CommandCenter/DealerDiscounts/Create")
);
const DiscountCodeDetails = lazy(
    () => import("@/pages/CommandCenter/DealerDiscounts/Details")
);

// Commission Tiers routes
const CommissionTiersList = lazy(
    () => import("@/pages/CommandCenter/CommissionTiers")
);
const CreateCommissionTier = lazy(
    () => import("@/pages/CommandCenter/CommissionTiers/Create")
);
const CommissionTierDetails = lazy(
    () => import("@/pages/CommandCenter/CommissionTiers/Details")
);

const CCProducts = lazy(() => import("@/pages/CommandCenter/Products"));
const EditProduct = lazy(
    () => import("@/pages/CommandCenter/Products/EditProduct")
);
const AddProduct = lazy(
    () => import("@/pages/CommandCenter/Products/AddProduct")
);

// Command Center Accounting
const CCAccounting = lazy(() => import("@/pages/CommandCenter/Accounting"));

// Command Center Invoices
const CCArAging = lazy(() => import("@/pages/CommandCenter/ArAging"));

const CCInvoices = lazy(() => import("@/pages/CommandCenter/Invoices"));

// Command Center Recurring Transactions
const CCRecurringTransactions = lazy(() => import("@/pages/CommandCenter/RecurringTransactions"));
const CCWalletBilling = lazy(() => import("@/pages/CommandCenter/WalletBilling"));
// Currency Settings + Bulk FX Pricing were merged into one "Currency & Pricing"
// screen (2026-08-11) — this one component serves the canonical route below and
// /cc/bulk-fx-pricing redirects to it.
const CCCurrencyAndPricing = lazy(() => import("@/pages/CommandCenter/CurrencySettings"));
const CCShippingRates = lazy(() => import("@/pages/CommandCenter/ShippingRates"));
const CCTaxRegistrations = lazy(() => import("@/pages/CommandCenter/TaxRegistrations"));
const CCUnitsSold = lazy(() => import("@/pages/CommandCenter/UnitsSold"));
const CCPermissionFlags = lazy(() => import("@/pages/CommandCenter/PermissionFlags"));
const CCFreeItems = lazy(() => import("@/pages/CommandCenter/FreeItems"));
const CCTaxCertificates = lazy(() => import("@/pages/CommandCenter/TaxCertificates"));
const CCRecurringTransactionDetails = lazy(() => import("@/pages/CommandCenter/RecurringTransactions/Details"));

// Command Center End Customer Details
const CCEndCustomerDetails = lazy(() => import("@/pages/CommandCenter/EndCustomers/Details"));

// Command Center Wallet Templates
const CCWalletTemplates = lazy(() => import("@/pages/Api/WalletTemplates"));
const CCWalletTemplatesDetails = lazy(() => import("@/pages/Api/WalletTemplates/Details"));

const CommandCenterRoutes = (
    <Fragment>
        <Route path={routes.comandCenter.arAging.root} element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]} allowedAccountTypes={[AccountType.INTERNAL]}><CCArAging /></ProtectedRoute>} />
        <Route path={routes.comandCenter.arAging.details} element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]} allowedAccountTypes={[AccountType.INTERNAL]}><CCArAging /></ProtectedRoute>} />
        {/* Command Center */}
        <Route
            path={routes.comandCenter.orders.root}
            element={
                <ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}>
                    <CCOrders />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.comandCenter.shipping.root}
            element={
                <ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}>
                    <CCShipping />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.comandCenter.orders.details}
            element={
                <ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}>
                    <CCOrderDetails />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.comandCenter.products.root}
            element={
                <ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}>
                    <CCProducts />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.comandCenter.products.create}
            element={
                <ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}>
                    <AddProduct />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.comandCenter.products.edit}
            element={
                <ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}>
                    <EditProduct />
                </ProtectedRoute>
            }
        />

        {/* Quotes */}
        <Route
            path={routes.comandCenter.quotes.root}
            element={
                <ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}>
                    <CCQuotes />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.comandCenter.quotes.details}
            element={
                <ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}>
                    <CCQuoteDetails />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.comandCenter.quotes.checkout}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CCQuoteCheckout /></ProtectedRoute>}
        />
        <Route
            path={routes.comandCenter.proposals.root}
            element={
                <ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}>
                    <CCProposals />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.comandCenter.projects.root}
            element={
                <ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}>
                    <CCProjects />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.comandCenter.calendar.root}
            element={
                <ProtectedRoute
                    requiredRoles={[UserRole.SUPER_ADMIN]}
                    allowedAccountTypes={[AccountType.INTERNAL]}
                >
                    <CCCalendar />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.comandCenter.projects.create}
            element={
                <ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}>
                    <CCProjectCreate />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.comandCenter.projects.details}
            element={
                <ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}>
                    <CCProjectDetails />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.comandCenter.projects.workOrderDetails}
            element={
                <ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}>
                    <CCProjectWorkOrderDetails />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.comandCenter.projectsPreview.root}
            element={
                <ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}>
                    <CCProjectsPreviewList />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.comandCenter.projectsPreview.details}
            element={
                <ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}>
                    <CCProjectsPreviewDetails />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.comandCenter.installerOrgs.root}
            element={
                <ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}>
                    <CCInstallerOrgs />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.comandCenter.installerOrgs.create}
            element={
                <ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}>
                    <CCInstallerOrgCreate />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.comandCenter.installerOrgs.details}
            element={
                <ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}>
                    <CCInstallerOrgDetails />
                </ProtectedRoute>
            }
        />
        {/* Dealer Discounts */}
        <Route
            path={routes.comandCenter.dealerDiscounts.root}
            element={
                <ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}>
                    <DealerDiscountsList />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.comandCenter.dealerDiscounts.create}
            element={
                <ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}>
                    <AddDiscountCode />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.comandCenter.dealerDiscounts.details}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><DiscountCodeDetails /></ProtectedRoute>}
        />

        {/* Commission Tiers */}
        <Route
            path={routes.comandCenter.commissionTiers.root}
            element={
                <ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}>
                    <CommissionTiersList />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.comandCenter.commissionTiers.create}
            element={
                <ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}>
                    <CreateCommissionTier />
                </ProtectedRoute>
            }
        />
        <Route
            path={routes.comandCenter.commissionTiers.details}
            element={
                <ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}>
                    <CommissionTierDetails />
                </ProtectedRoute>
            }
        />

        {/* Accounts */}
        <Route
            path={routes.comandCenter.accounts.root}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><AccountList /></ProtectedRoute>}
        />
        <Route
            path={routes.comandCenter.accounts.details}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><AccountDetails /></ProtectedRoute>}
        />

        <Route
            path={routes.comandCenter.accounts.create}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CreateAccount /></ProtectedRoute>}
        />

        {/* Command Center Leads */}
        <Route
            path={routes.comandCenter.leads.root}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CCLeads /></ProtectedRoute>}
        />
        <Route
            path={routes.comandCenter.leads.convert}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CCConvertLead /></ProtectedRoute>}
        />

        <Route
            path={routes.comandCenter.leads.details}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CCLeadDetails /></ProtectedRoute>}
        />

        <Route
            path={routes.comandCenter.leads.create}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CreateLead /></ProtectedRoute>}
        />

        <Route
            path={routes.comandCenter.leads.upload}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><UploadLeads /></ProtectedRoute>}
        />

        {/* Command Center Opportunities */}
        <Route
            path={routes.comandCenter.opportunities.root}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CCOpportunities /></ProtectedRoute>}
        />
        <Route
            path={routes.comandCenter.opportunities.details}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CCOpportunityDetails /></ProtectedRoute>}
        />
        <Route
            path={routes.comandCenter.opportunities.create}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CCOpportunityCreate /></ProtectedRoute>}
        />

        {/* Command Center Users */}
        <Route
            path={routes.comandCenter.users.root}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CCUsers /></ProtectedRoute>}
        />
        <Route
            path={routes.comandCenter.users.create}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CCAddEditUser /></ProtectedRoute>}
        />
        <Route
            path={routes.comandCenter.users.edit}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CCUserDetails /></ProtectedRoute>}
        />

        {/* Command Center Logs */}
        <Route
            path={routes.comandCenter.logs.root}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CCLogs /></ProtectedRoute>}
        />
        <Route
            path={routes.comandCenter.logs.details}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CCLogDetails /></ProtectedRoute>}
        />

        {/* Command Center Accounting */}
        <Route
            path={routes.comandCenter.accounting.root}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CCAccounting /></ProtectedRoute>}
        />

        {/* Invoices */}
        <Route
            path={routes.comandCenter.invoices.root}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CCInvoices /></ProtectedRoute>}
        />

        {/* Recurring Transactions */}
        <Route
            path={routes.comandCenter.recurringTransactions.root}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CCRecurringTransactions /></ProtectedRoute>}
        />
        <Route
            path={routes.comandCenter.recurringTransactions.details}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CCRecurringTransactionDetails /></ProtectedRoute>}
        />

        {/* Wallet Provisioning & Billing Report */}
        <Route
            path={routes.comandCenter.walletBilling.root}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CCWalletBilling /></ProtectedRoute>}
        />

        {/* Units Sold Report */}
        <Route
            path={routes.comandCenter.unitsSold.root}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CCUnitsSold /></ProtectedRoute>}
        />

        {/* Free Items Report (15 Oct call, item 12) */}
        <Route
            path={routes.comandCenter.freeItems.root}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CCFreeItems /></ProtectedRoute>}
        />

        {/* Currency & Pricing — the merged Currency Settings + Bulk FX screen */}
        <Route
            path={routes.comandCenter.currencySettings.root}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CCCurrencyAndPricing /></ProtectedRoute>}
        />

        {/* Shipping Rates — the per-country weight/cost rate tables quoting reads */}
        <Route
            path={routes.comandCenter.shippingRates.root}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CCShippingRates /></ProtectedRoute>}
        />

        {/* Tax Registrations — where we are registered to collect tax, i.e. the
            on-switch that decides whether a destination is taxed at all */}
        <Route
            path={routes.comandCenter.taxRegistrations.root}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CCTaxRegistrations /></ProtectedRoute>}
        />

        {/* Permission Flags — the six ex-env-var email allowlists, editable.
            SUPER_ADMIN is the FLOOR for reaching the page, not the edit gate:
            writing additionally requires membership in PERMISSION_ADMIN_EMAILS,
            enforced server-side and surfaced as `canEdit` so the page renders
            read-only. Per design §3.3 the role floor exists because the CC nav
            group is role-gated and the menu filter cannot express "this email is
            on a list" — and it is the direction that fails safe. */}
        <Route
            path={routes.comandCenter.permissionFlags.root}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CCPermissionFlags /></ProtectedRoute>}
        />

        {/* Tax Exemption Certificates — SUPER_ADMIN review queue for
            partner-uploaded resale/exempt-org/government certificates
            (Task 12, spec §6/§7.3) */}
        <Route
            path={routes.comandCenter.taxCertificates.root}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CCTaxCertificates /></ProtectedRoute>}
        />

        {/* Legacy Bulk FX Pricing path — kept as a redirect so existing
            bookmarks/links don't 404 now that the two screens are one. */}
        <Route
            path={routes.comandCenter.bulkFxPricing.root}
            element={<Navigate to={routes.comandCenter.currencySettings.root} replace />}
        />

        {/* End Customer Details */}
        <Route
            path={routes.comandCenter.endCustomers.details}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CCEndCustomerDetails /></ProtectedRoute>}
        />

        {/* Command Center Wallet Templates */}
        <Route
            path={routes.comandCenter.walletTemplates.root}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CCWalletTemplates /></ProtectedRoute>}
        />
        <Route
            path={routes.comandCenter.walletTemplates.details}
            element={<ProtectedRoute requiredRoles={[UserRole.SUPER_ADMIN]}><CCWalletTemplatesDetails /></ProtectedRoute>}
        />
    </Fragment>
);

export default CommandCenterRoutes;
